import React from 'react';
import { Empty } from 'antd';
import type { DockingRecord } from '../../types/docking';
import type { MarketProject } from '../../types/market';
import {
  completionStatusLabel,
  formatOverdueDisplay,
  isRecordOverdue,
} from '../../utils/progressHelpers';
import {
  ProjectContextLinks,
  type ProjectContextMode,
} from './ProjectContextLinks';

export interface ProgressDetailPanelProps {
  record: DockingRecord | null;
  /** 已选项目（无进展条目时仍可展示跳转） */
  selectedProjectId?: string | null;
  projects: MarketProject[];
  dockingCount?: number;
  progressCount?: number;
  incompleteProgressCount?: number;
  onNavigateRelated?: (mode: ProjectContextMode) => void;
}

function DetailRow(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="dash-detail-row">
      <div className="dash-detail-row__label">{props.label}</div>
      <div className="dash-detail-row__value">{props.children}</div>
    </div>
  );
}

export function ProgressDetailPanel(
  props: ProgressDetailPanelProps,
): React.ReactElement {
  const {
    record,
    selectedProjectId,
    projects,
    dockingCount = 0,
    progressCount = 0,
    incompleteProgressCount = 0,
    onNavigateRelated,
  } = props;

  const projectId = record?.projectId ?? selectedProjectId ?? null;
  const project = projectId
    ? projects.find((p) => p.id === projectId)
    : undefined;

  const links =
    projectId && onNavigateRelated ? (
      <ProjectContextLinks
        current="progress"
        dockingCount={dockingCount}
        progressCount={progressCount}
        incompleteProgressCount={incompleteProgressCount}
        onNavigate={onNavigateRelated}
      />
    ) : null;

  if (!record) {
    return (
      <aside className="dash-panel dash-panel--right">
        {projectId ? (
          <>
            <div className="dash-detail-title">
              {project?.name ?? '项目'}的工作进展
            </div>
            <div className="dash-detail-body dash-detail-body--structured">
              {links}
              <div className="dash-detail-empty" style={{ minHeight: 160 }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span className="dash-empty-text">
                      {selectedProjectId
                        ? '该项目暂无工作进展记录'
                        : '请在左侧选择一条进展记录'}
                    </span>
                  }
                />
              </div>
            </div>
          </>
        ) : (
          <div className="dash-detail-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="dash-empty-text">
                  请在左侧选择一条进展记录
                </span>
              }
            />
          </div>
        )}
      </aside>
    );
  }

  const overdue = isRecordOverdue(record);
  const statusClass =
    record.completionStatus === 'completed'
      ? 'progress-tag progress-tag--done'
      : record.completionStatus === 'incomplete'
        ? 'progress-tag progress-tag--todo'
        : 'progress-tag progress-tag--muted';

  return (
    <aside className="dash-panel dash-panel--right">
      <div className="dash-detail-title">{record.projectName}</div>
      <div className="dash-detail-body dash-detail-body--structured">
        {links}
        <div className="dash-detail-hero__tags" style={{ marginBottom: 12 }}>
          <span className={statusClass}>
            {completionStatusLabel(record.completionStatus)}
          </span>
          {overdue ? (
            <span className="progress-tag progress-tag--overdue">
              已逾期 {formatOverdueDisplay(record)}
            </span>
          ) : record.overdueLabel || record.overdueDays != null ? (
            <span className="progress-tag progress-tag--ok">
              {formatOverdueDisplay(record)}
            </span>
          ) : null}
        </div>

        <DetailRow label="对接日期">{record.dockedAt || '—'}</DetailRow>
        <DetailRow label="计划完成时间">
          {record.plannedCompletedAt || '—'}
        </DetailRow>
        <DetailRow label="已逾期天数">
          {formatOverdueDisplay(record)}
        </DetailRow>
        <DetailRow label="是否完成">
          {completionStatusLabel(record.completionStatus)}
        </DetailRow>

        <div className="dash-detail-section" style={{ marginTop: 16 }}>
          <div className="dash-detail-section__title">后续工作安排</div>
          {record.followUpPlan ? (
            <div className="dash-detail-overview__text">{record.followUpPlan}</div>
          ) : (
            <div className="dash-detail-footnote">暂无后续工作安排</div>
          )}
        </div>
      </div>
    </aside>
  );
}
